// src/components/routes/RouteBuilderModal.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Route, Search, Loader2, CheckCircle2, Clock, Package, AlertTriangle } from "lucide-react";
import { getDocs, collection, getDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";

// ---------------- Helpers ----------------
const norm = (s) => String(s || "").trim();
const normLower = (s) => norm(s).toLowerCase();
const normZip5 = (z) => norm(z).replace(/[^\d]/g, "").slice(0, 5);
const isMobileUA = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const stateTo2 = (v) => {
  const raw = String(v || "").trim().toUpperCase();
  if (!raw) return "";
  if (raw.length === 2) return raw;
  return raw.slice(0, 2);
};

const joinParts = (parts) => parts.map(norm).filter(Boolean).join(", ");

function getAccountLabel(a) {
  return (
    a?.name ||
    a?.accountName ||
    a?.title ||
    a?.restaurantName ||
    a?.businessName ||
    a?.companyName ||
    a?.id ||
    "Account"
  );
}

/**
 * Contact extraction (tries common schema shapes; falls back to email/phone).
 */
function accountContactLine(a) {
  const contactName =
    norm(a?.contactName) ||
    norm(a?.primaryContactName) ||
    norm(a?.contact) ||
    norm(a?.pointOfContact) ||
    norm(a?.pocName) ||
    norm(a?.extraFields?.contactName) ||
    norm(a?.extraFields?.primaryContactName) ||
    "";

  const email =
    norm(a?.email) ||
    norm(a?.contactEmail) ||
    norm(a?.primaryEmail) ||
    norm(a?.extraFields?.email) ||
    "";

  const phone =
    norm(a?.phone) ||
    norm(a?.phoneNumber) ||
    norm(a?.mobile) ||
    norm(a?.extraFields?.phone) ||
    "";

  if (contactName) return contactName;
  if (email && phone) return `${email} • ${phone}`;
  return email || phone || "";
}

/**
 * Build address string from a Firestore map/object.
 */
function addressFromObject(obj) {
  if (!isPlainObject(obj)) return "";

  const line1 =
    obj?.line1 ||
    obj?.address1 ||
    obj?.street ||
    obj?.street1 ||
    obj?.addressLine1 ||
    obj?.line_1 ||
    "";
  const line2 =
    obj?.line2 || obj?.address2 || obj?.street2 || obj?.addressLine2 || obj?.line_2 || "";
  const city = obj?.city || "";
  const state = obj?.state || obj?.stateCode || "";
  const zip = obj?.zip || obj?.zipCode || obj?.postalCode || "";
  const country = obj?.country || "";

  return joinParts([line1, line2, city, state, zip, country]);
}

/**
 * Address extraction:
 * - If address is a string => use it
 * - If address is a map => build from its fields
 * - Else fall back to top-level line1/line2/city/state/zip/country
 */
function accountToAddress(a) {
  const addr = a?.address;

  if (typeof addr === "string" && norm(addr)) return norm(addr);

  if (isPlainObject(addr)) {
    const built = addressFromObject(addr);
    if (norm(built)) return built;
  }

  const single =
    (typeof a?.fullAddress === "string" && a.fullAddress) ||
    (typeof a?.formattedAddress === "string" && a.formattedAddress) ||
    (typeof a?.addressText === "string" && a.addressText) ||
    "";
  if (norm(single)) return norm(single);

  const line1 =
    a?.line1 ||
    a?.address1 ||
    a?.street ||
    a?.street1 ||
    a?.addressLine1 ||
    a?.line_1 ||
    "";
  const line2 = a?.line2 || a?.address2 || a?.street2 || a?.addressLine2 || a?.line_2 || "";
  const city = a?.city || "";
  const state = a?.state || a?.stateCode || "";
  const zip = a?.zip || a?.postalCode || a?.zipCode || "";
  const country = a?.country || "";

  const parts = [line1, line2, city, state, zip, country].map(norm).filter(Boolean);
  return parts.length ? parts.join(", ") : "";
}

function accountCity(a) {
  return norm(a?.city || a?.address?.city || "");
}
function accountState(a) {
  return stateTo2(a?.state || a?.stateCode || a?.address?.state || a?.address?.stateCode || "");
}
function accountZip(a) {
  return normZip5(
    a?.zip ||
      a?.zipCode ||
      a?.postalCode ||
      a?.address?.zip ||
      a?.address?.zipCode ||
      a?.address?.postalCode ||
      ""
  );
}

function normalizeForMaps(s) {
  return norm(s).replace(/\s+/g, " ").trim();
}

function isLatLngString(s) {
  const v = normalizeForMaps(s);
  return /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(v);
}

function isFollowUpAccount(a) {
  return (
    a?.followUp === true ||
    a?.needsFollowUp === true ||
    a?.follow_up === true ||
    a?.flags?.followUp === true ||
    String(a?.status || "").toLowerCase().includes("follow")
  );
}

/**
 * Territory matching:
 * - expects territory.states (["VA"]), territory.cities (["Norfolk"]), territory.zipCodes (["23503"])
 * - match if ANY matches (state OR city OR zip).
 * - no criteria => match all.
 */
function matchesTerritory(account, territory) {
  if (!territory) return true;

  const tStates = Array.isArray(territory.states)
    ? territory.states.map((s) => String(s).toUpperCase())
    : [];
  const tCities = Array.isArray(territory.cities)
    ? territory.cities.map((c) => normLower(c))
    : [];
  const tZips = Array.isArray(territory.zipCodes)
    ? territory.zipCodes.map((z) => normZip5(z))
    : [];

  const hasCriteria = tStates.length || tCities.length || tZips.length;
  if (!hasCriteria) return true;

  const st = accountState(account);
  const ct = normLower(accountCity(account));
  const zp = accountZip(account);

  if (st && tStates.includes(st)) return true;
  if (ct && tCities.includes(ct)) return true;
  if (zp && tZips.includes(zp)) return true;

  return false;
}

/**
 * Simple sampleCounts by account from companies/{id}/sampleOrders.
 */
async function loadSampleCountsByAccount(companyId) {
  const counts = {};
  try {
    const snap = await getDocs(collection(db, "companies", companyId, "sampleOrders"));
    snap.docs.forEach((d) => {
      const data = d.data() || {};
      const aid = data.accountId || data.accountID || data.account || "";
      if (!aid) return;
      counts[aid] = (counts[aid] || 0) + 1;
    });
    return counts;
  } catch {
    return {};
  }
}

// ---------------- Google Maps URL Builder ----------------
// Reliability:
// - If origin is "" => omit origin (Maps uses device location)
// - Waypoints joined by "|"
// - optimize:true supported in waypoints param
function buildGoogleMapsDirUrl({ origin, destination, waypoints = [], optimize = false }) {
  const base = "https://www.google.com/maps/dir/?api=1&travelmode=driving";
  const params = [];

  const dest = normalizeForMaps(destination);
  if (!dest) throw new Error("Destination is empty.");

  const org = normalizeForMaps(origin || "");
  if (org) params.push(`origin=${encodeURIComponent(org)}`);

  params.push(`destination=${encodeURIComponent(dest)}`);

  const wps = (waypoints || []).map(normalizeForMaps).filter(Boolean);
  if (wps.length) {
    const wpString = optimize ? `optimize:true|${wps.join("|")}` : wps.join("|");
    params.push(`waypoints=${encodeURIComponent(wpString)}`);
  }

  return `${base}&${params.join("&")}`;
}

// ---------------- Geo helpers ----------------
function getCurrentPositionPromise({ timeout = 6500 } = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not available in this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos?.coords?.latitude;
        const lng = pos?.coords?.longitude;
        if (typeof lat === "number" && typeof lng === "number") resolve({ lat, lng });
        else reject(new Error("Could not read current location."));
      },
      (err) => reject(err || new Error("Location permission denied.")),
      { enableHighAccuracy: false, timeout, maximumAge: 60000 }
    );
  });
}
const latLngToString = (p) => `${p.lat},${p.lng}`;

// ---------------- Route estimation + geocoding (best-effort) ----------------
let _gmapsPromise = null;

function getGoogleMapsKey() {
  return (
    process.env.REACT_APP_GOOGLE_MAPS_KEY ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.REACT_APP_GOOGLE_KEY ||
    ""
  );
}

function ensureGoogleMapsLoaded() {
  if (window.google?.maps?.DirectionsService) return Promise.resolve(window.google);
  const key = getGoogleMapsKey();
  if (!key) return Promise.reject(new Error("Missing Google Maps key. Set REACT_APP_GOOGLE_MAPS_KEY."));

  if (_gmapsPromise) return _gmapsPromise;

  _gmapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-gmaps-loader='1']");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", () => reject(new Error("Google Maps script failed to load")));
      return;
    }

    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.dataset.gmapsLoader = "1";

    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error("Google Maps script failed to load"));

    document.head.appendChild(s);
  });

  return _gmapsPromise;
}

async function estimateRouteMinutes({ origin, destination, waypoints, optimizeWaypoints }) {
  const google = await ensureGoogleMapsLoaded();
  const svc = new google.maps.DirectionsService();

  const req = {
    origin,
    destination,
    waypoints: (waypoints || []).map((w) => ({ location: w, stopover: true })),
    optimizeWaypoints: !!optimizeWaypoints,
    travelMode: google.maps.TravelMode.DRIVING,
  };

  return new Promise((resolve, reject) => {
    svc.route(req, (result, status) => {
      if (status !== "OK" || !result?.routes?.[0]?.legs) {
        reject(new Error(`Directions failed: ${status}`));
        return;
      }
      const legs = result.routes[0].legs || [];
      const seconds = legs.reduce((sum, leg) => sum + (leg?.duration?.value || 0), 0);
      resolve(Math.max(1, Math.round(seconds / 60)));
    });
  });
}

async function geocodeOneAddress({ geocoder, address }) {
  const a = normalizeForMaps(address);
  if (!a) return null;
  if (isLatLngString(a)) return a;

  for (let attempt = 0; attempt < 3; attempt++) {
    // eslint-disable-next-line no-await-in-loop
    const res = await new Promise((resolve) => {
      geocoder.geocode({ address: a }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          resolve({ ok: true, formatted: results[0].formatted_address || a });
        } else if (status === "OVER_QUERY_LIMIT") {
          resolve({ ok: false, over: true });
        } else {
          resolve({ ok: false, over: false });
        }
      });
    });

    if (res.ok) return res.formatted;

    if (res.over) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(250 + attempt * 250);
      continue;
    }
    return null;
  }

  return null;
}

function formatSkipped(labels) {
  const arr = (labels || []).filter(Boolean);
  if (arr.length <= 3) return arr.join(", ");
  const first = arr.slice(0, 3).join(", ");
  return `${first} +${arr.length - 3} more`;
}

// ---------------- UI components ----------------
function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-xs font-semibold text-slate-700 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <div className="text-[11px] font-medium text-slate-600 mb-1">{label}</div>
      {children}
      {hint ? <div className="mt-1 text-[11px] text-slate-500">{hint}</div> : null}
    </div>
  );
}

function Toggle({ label, checked, onChange, hint }) {
  return (
    <div>
      <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2">
        <span className="text-sm text-slate-700">{label}</span>
        <input type="checkbox" checked={checked} onChange={onChange} />
      </label>
      {hint ? <div className="mt-1 text-[11px] text-slate-500">{hint}</div> : null}
    </div>
  );
}

function ConfirmModal({ open, title, message, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-3">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div className="text-sm font-semibold text-slate-900">{title}</div>
        </div>
        <div className="p-4 text-sm text-slate-700 whitespace-pre-line">{message}</div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-sm font-semibold"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ✅ prefill: {
 *   territoryId?: string,
 *   sampleFilter?: "any"|"with"|"without",
 *   followUpOnly?: boolean,
 *   accountIds?: string[],
 *   autoPickFromTerritory?: boolean
 * }
 */
export default function RouteBuilderModal({ open, onClose, companyId, me, prefill = null }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  const [territories, setTerritories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [sampleCountsByAccount, setSampleCountsByAccount] = useState({});
  const [loadingSamples, setLoadingSamples] = useState(false);

  const [officeAddress, setOfficeAddress] = useState("");
  const [warehouseAddress, setWarehouseAddress] = useState("");

  const [territoryId, setTerritoryId] = useState("");
  const [sampleFilter, setSampleFilter] = useState("any"); // any | with | without
  const [followUpOnly, setFollowUpOnly] = useState(false);
  const [optimizeStops, setOptimizeStops] = useState(true);

  const [returnToStart, setReturnToStart] = useState(false);

  const [q, setQ] = useState("");
  const [autoPickFromTerritory, setAutoPickFromTerritory] = useState(true);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);

  const [startMode, setStartMode] = useState("current"); // current | office | warehouse | custom
  const [customStart, setCustomStart] = useState("");

  const [maxStops, setMaxStops] = useState(10);
  const MAX_SAFE_STOPS = 20;
  const effectiveMaxStops = Math.min(Math.max(1, Number(maxStops) || 10), MAX_SAFE_STOPS);

  const [maxTravelMinutes, setMaxTravelMinutes] = useState(0);
  const [estimating, setEstimating] = useState(false);
  const [lastEstimate, setLastEstimate] = useState(null);
  const estimateAbortRef = useRef({ token: 0 });

  const [openSameTab, setOpenSameTab] = useState(isMobileUA());

  // custom confirm flow for same-tab navigation
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const activeTerritory = useMemo(() => {
    if (!territoryId) return null;
    return territories.find((t) => t.id === territoryId) || null;
  }, [territoryId, territories]);

  // Load data on open
  useEffect(() => {
    if (!open) return;
    if (!companyId) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setErr("");
      setNotice("");

      try {
        try {
          const compSnap = await getDoc(doc(db, "companies", companyId));
          if (compSnap.exists()) {
            const c = compSnap.data() || {};
            if (!cancelled) {
              const office =
                typeof c.officeAddress === "string"
                  ? c.officeAddress
                  : isPlainObject(c.officeAddress)
                  ? addressFromObject(c.officeAddress)
                  : (typeof c.hqAddress === "string" ? c.hqAddress : "") ||
                    (typeof c.address === "string" ? c.address : "");
              const wh =
                typeof c.warehouseAddress === "string"
                  ? c.warehouseAddress
                  : isPlainObject(c.warehouseAddress)
                  ? addressFromObject(c.warehouseAddress)
                  : "";

              setOfficeAddress(norm(office));
              setWarehouseAddress(norm(wh));
            }
          }
        } catch (e) {
          console.warn("[RouteBuilderModal] company doc load failed:", e);
        }

        const terrSnap = await getDocs(collection(db, "companies", companyId, "territories"));
        const terrList = terrSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        if (!cancelled) setTerritories(terrList);

        const accSnap = await getDocs(collection(db, "companies", companyId, "accounts"));
        const accList = accSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => getAccountLabel(a).localeCompare(getAccountLabel(b)));
        if (!cancelled) setAccounts(accList);

        setLoadingSamples(true);
        const counts = await loadSampleCountsByAccount(companyId);
        if (!cancelled) setSampleCountsByAccount(counts);
      } catch (e) {
        console.error("[RouteBuilderModal] load failed:", e);
        if (!cancelled) setErr(e?.message || "Failed to load route data.");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingSamples(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  // Reset + apply prefill on open
  useEffect(() => {
    if (!open) return;

    const p = prefill || {};
    const pTerritoryId = norm(p.territoryId || "");
    const pSampleFilter =
      p.sampleFilter === "with" || p.sampleFilter === "without" ? p.sampleFilter : "any";
    const pFollowUp = !!p.followUpOnly;
    const pAccountIds = Array.isArray(p.accountIds) ? p.accountIds.filter(Boolean) : [];

    setTerritoryId(pTerritoryId);
    setSampleFilter(pSampleFilter);
    setFollowUpOnly(pFollowUp);
    setOptimizeStops(true);
    setReturnToStart(false);

    setQ("");

    const autoPick =
      typeof p.autoPickFromTerritory === "boolean"
        ? p.autoPickFromTerritory
        : pTerritoryId && pAccountIds.length === 0;

    setAutoPickFromTerritory(!!autoPick);
    setSelectedAccountIds(pAccountIds);

    setStartMode("current");
    setCustomStart("");
    setMaxStops(10);
    setMaxTravelMinutes(0);
    setLastEstimate(null);
    setEstimating(false);
    setOpenSameTab(isMobileUA());
    setErr("");
    setNotice("");

    setConfirmState({ open: false, title: "", message: "", onConfirm: null });
  }, [open, prefill]);

  const filteredAccounts = useMemo(() => {
    const queryText = normLower(q);

    let list = Array.isArray(accounts) ? [...accounts] : [];

    if (activeTerritory) list = list.filter((a) => matchesTerritory(a, activeTerritory));
    if (followUpOnly) list = list.filter((a) => isFollowUpAccount(a));

    if (sampleFilter === "with") {
      list = list.filter((a) => (sampleCountsByAccount[a.id] || 0) > 0);
    } else if (sampleFilter === "without") {
      list = list.filter((a) => (sampleCountsByAccount[a.id] || 0) === 0);
    }

    if (queryText) {
      list = list.filter((a) => {
        const label = normLower(getAccountLabel(a));
        const addr = normLower(accountToAddress(a));
        const city = normLower(accountCity(a));
        const st = normLower(accountState(a));
        const zip = normLower(accountZip(a));
        const contact = normLower(accountContactLine(a));
        const email = normLower(a?.email || "");
        const phone = normLower(a?.phone || "");
        return (
          label.includes(queryText) ||
          addr.includes(queryText) ||
          city.includes(queryText) ||
          st.includes(queryText) ||
          zip.includes(queryText) ||
          contact.includes(queryText) ||
          email.includes(queryText) ||
          phone.includes(queryText)
        );
      });
    }

    // only routable accounts (have some address string)
    list = list.filter((a) => !!norm(accountToAddress(a)));

    // stable-ish sort: zip then name
    list.sort((a, b) => {
      const az = accountZip(a);
      const bz = accountZip(b);
      if (az && bz && az !== bz) return az.localeCompare(bz);
      return getAccountLabel(a).localeCompare(getAccountLabel(b));
    });

    return list;
  }, [accounts, activeTerritory, followUpOnly, sampleFilter, q, sampleCountsByAccount]);

  // Auto-pick from territory
  useEffect(() => {
    if (!open) return;
    if (!autoPickFromTerritory) return;
    if (territoryId) setSelectedAccountIds(filteredAccounts.map((a) => a.id));
  }, [open, autoPickFromTerritory, territoryId, filteredAccounts]);

  const selectedAccounts = useMemo(() => {
    const set = new Set(selectedAccountIds);
    return filteredAccounts.filter((a) => set.has(a.id));
  }, [filteredAccounts, selectedAccountIds]);

  const stops = useMemo(() => {
    const raw = selectedAccounts
      .map((a) => ({
        id: a.id,
        label: getAccountLabel(a),
        address: normalizeForMaps(accountToAddress(a)),
      }))
      .filter((x) => norm(x.address));

    return raw.slice(0, effectiveMaxStops);
  }, [selectedAccounts, effectiveMaxStops]);

  const toggleSelected = (id) => {
    setSelectedAccountIds((prev) => {
      const s = new Set(prev || []);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return Array.from(s);
    });
  };

  function getOriginLabel() {
    if (startMode === "current") return "Current Location";
    if (startMode === "office") return officeAddress || "(set office address)";
    if (startMode === "warehouse") return warehouseAddress || "(set warehouse address)";
    if (startMode === "custom") return customStart || "(enter start address)";
    return "Current Location";
  }

  function openMaps(url) {
    if (openSameTab) window.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
  }

  function buildUrlFromStops({ startModeNow, startAddress, startLatLngString, chosenStops }) {
    if (!chosenStops.length) throw new Error("No valid stops remain after address checking.");

    // Round trip: destination = origin; waypoints = all stops
    if (returnToStart) {
      const originStr =
        startModeNow === "current" ? norm(startLatLngString) : normalizeForMaps(startAddress || "");

      if (!originStr) {
        throw new Error(
          "Round trip needs a known start. Allow location access (Current) or choose Office/Warehouse/Custom."
        );
      }

      const waypoints = chosenStops.map((s) => s.address).filter(Boolean);
      return buildGoogleMapsDirUrl({
        origin: originStr,
        destination: originStr,
        waypoints,
        optimize: optimizeStops && waypoints.length > 1,
      });
    }

    // Not round trip:
    // - Current: omit origin param (maps uses device location)
    // - Others: origin = start address
    const origin = startModeNow === "current" ? "" : normalizeForMaps(startAddress || "");
    if (startModeNow !== "current" && !origin) {
      throw new Error("Start address is empty.");
    }

    if (chosenStops.length === 1) {
      return buildGoogleMapsDirUrl({
        origin,
        destination: chosenStops[0].address,
        waypoints: [],
        optimize: false,
      });
    }

    const destination = chosenStops[chosenStops.length - 1].address;
    const waypoints = chosenStops.slice(0, chosenStops.length - 1).map((s) => s.address);

    return buildGoogleMapsDirUrl({
      origin,
      destination,
      waypoints,
      optimize: optimizeStops && waypoints.length > 1,
    });
  }

  async function chooseStopsUnderTime({ originEstimate, allStops, maxMinutes, isRoundTrip }) {
    if (!maxMinutes || maxMinutes <= 0) return { usedStops: allStops, minutes: null, capped: false };
    if (!originEstimate) return { usedStops: allStops, minutes: null, capped: false };

    const estimateSubset = async (subset) => {
      if (!subset.length) return 0;

      if (isRoundTrip) {
        const waypoints = subset.map((s) => s.address);
        return estimateRouteMinutes({
          origin: originEstimate,
          destination: originEstimate,
          waypoints,
          optimizeWaypoints: optimizeStops && waypoints.length > 1,
        });
      }

      if (subset.length === 1) {
        return estimateRouteMinutes({
          origin: originEstimate,
          destination: subset[0].address,
          waypoints: [],
          optimizeWaypoints: false,
        });
      }

      const destination = subset[subset.length - 1].address;
      const waypoints = subset.slice(0, subset.length - 1).map((s) => s.address);

      return estimateRouteMinutes({
        origin: originEstimate,
        destination,
        waypoints,
        optimizeWaypoints: optimizeStops && waypoints.length > 1,
      });
    };

    let lo = 1;
    let hi = allStops.length;
    let bestCount = 1;
    let bestMinutes = null;

    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const subset = allStops.slice(0, mid);
      // eslint-disable-next-line no-await-in-loop
      const mins = await estimateSubset(subset);

      if (mins <= maxMinutes) {
        bestCount = mid;
        bestMinutes = mins;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    const usedStops = allStops.slice(0, bestCount);
    return { usedStops, minutes: bestMinutes, capped: bestCount < allStops.length };
  }

  const handleBuildRoute = async () => {
    setErr("");
    setNotice("");
    setLastEstimate(null);

    if (stops.length === 0) {
      setErr("Pick at least one account (and make sure it has an address).");
      return;
    }

    if (startMode === "office" && !norm(officeAddress)) {
      setErr("Office start selected, but officeAddress is empty. Enter an address.");
      return;
    }
    if (startMode === "warehouse" && !norm(warehouseAddress)) {
      setErr("Warehouse start selected, but warehouseAddress is empty. Enter an address.");
      return;
    }
    if (startMode === "custom" && !norm(customStart)) {
      setErr("Custom start selected, but start address is empty. Enter an address.");
      return;
    }

    setEstimating(true);
    const token = (estimateAbortRef.current.token || 0) + 1;
    estimateAbortRef.current.token = token;

    try {
      const startAddress =
        startMode === "office"
          ? officeAddress
          : startMode === "warehouse"
          ? warehouseAddress
          : startMode === "custom"
          ? customStart
          : "";

      // If round trip + current, we MUST get lat/lng
      let startLatLngString = "";
      if (returnToStart && startMode === "current") {
        try {
          const pos = await getCurrentPositionPromise({ timeout: 6500 });
          startLatLngString = latLngToString(pos);
        } catch {
          setErr(
            "Round trip needs location access when Start = Current Location. Allow location access or choose Office/Warehouse/Custom."
          );
          return;
        }
      }

      if (estimateAbortRef.current.token !== token) return;

      // --- Best-effort Google availability (for geocoding + estimation) ---
      let google = null;
      try {
        google = await ensureGoogleMapsLoaded();
      } catch {
        google = null;
      }

      if (estimateAbortRef.current.token !== token) return;

      // --- Validate / normalize stops by geocoding (SKIP if unrecognized) ---
      let checkedStops = stops;
      const skipped = [];
      let skipMsg = "";

      if (google?.maps?.Geocoder) {
        const geocoder = new google.maps.Geocoder();

        // Validate start address if not current (use formatted version for reliability)
        if (startMode !== "current") {
          const startOk = await geocodeOneAddress({ geocoder, address: startAddress });
          if (estimateAbortRef.current.token !== token) return;

          if (!startOk) {
            setErr(
              "Google Maps couldn’t recognize your selected start address. Please edit it (or use Current Location)."
            );
            return;
          }
          if (startMode === "office") setOfficeAddress(startOk);
          if (startMode === "warehouse") setWarehouseAddress(startOk);
          if (startMode === "custom") setCustomStart(startOk);
        }

        const validated = [];
        for (const s of stops) {
          if (estimateAbortRef.current.token !== token) return;

          // eslint-disable-next-line no-await-in-loop
          const ok = await geocodeOneAddress({ geocoder, address: s.address });
          if (ok) validated.push({ ...s, address: ok });
          else skipped.push(s);
        }

        checkedStops = validated;

        if (skipped.length) {
          skipMsg = `Skipped ${skipped.length} stop(s) with unrecognized addresses: ${formatSkipped(
            skipped.map((x) => x.label)
          )}`;
          setNotice(skipMsg);
        }
      } else {
        checkedStops = stops.filter((s) => {
          const a = norm(s.address);
          return a && a !== "[object Object]" && !a.toLowerCase().includes("object object");
        });
      }

      if (estimateAbortRef.current.token !== token) return;

      if (checkedStops.length === 0) {
        setErr("No valid addresses were found after checking your selected stops.");
        return;
      }

      // --- Time cap origin for estimating (only if needed) ---
      let originEstimate = null;
      if (maxTravelMinutes > 0) {
        if (startMode === "current") {
          if (startLatLngString) {
            originEstimate = startLatLngString;
          } else {
            try {
              const pos = await getCurrentPositionPromise({ timeout: 6500 });
              originEstimate = { lat: pos.lat, lng: pos.lng };
            } catch {
              originEstimate = null;
              setNotice(
                "Heads up: time cap needs location access when Start = Current Location. Route will still open, but won’t auto-trim."
              );
            }
          }
        } else {
          originEstimate = startAddress;
        }
      }

      if (estimateAbortRef.current.token !== token) return;

      // --- Apply time cap trimming (if possible) ---
      let chosenStops = checkedStops;
      let minutes = null;
      let capped = false;

      if (maxTravelMinutes > 0 && originEstimate) {
        try {
          const res = await chooseStopsUnderTime({
            originEstimate,
            allStops: checkedStops,
            maxMinutes: maxTravelMinutes,
            isRoundTrip: returnToStart,
          });
          if (estimateAbortRef.current.token !== token) return;

          chosenStops = res.usedStops;
          minutes = res.minutes;
          capped = res.capped;
        } catch (e) {
          console.warn("[RouteBuilderModal] time cap estimation failed:", e);
          chosenStops = checkedStops;
          minutes = null;
          capped = false;
        }
      }

      setLastEstimate({
        minutes: minutes ?? null,
        usedStops: chosenStops.length,
        capped,
      });

      const finalStartAddress =
        startMode === "office"
          ? officeAddress
          : startMode === "warehouse"
          ? warehouseAddress
          : startMode === "custom"
          ? customStart
          : "";

      const url = buildUrlFromStops({
        startModeNow: startMode,
        startAddress: finalStartAddress,
        startLatLngString,
        chosenStops,
      });

      // ✅ Same-tab + skipped => show custom confirm before navigating away
      if (openSameTab && skipped.length && skipMsg) {
        setConfirmState({
          open: true,
          title: "Some stops were skipped",
          message: `${skipMsg}\n\nContinue to Google Maps with the remaining stops?`,
          onConfirm: () => {
            openMaps(url);
            setConfirmState({ open: false, title: "", message: "", onConfirm: null });
            onClose?.();
          },
        });
        return;
      }

      // Otherwise open immediately
      openMaps(url);

      // ✅ If new tab + skipped, keep modal open so they can see notice.
      if (!openSameTab && skipped.length) return;

      onClose?.();
    } catch (e) {
      console.error("[RouteBuilderModal] build route failed:", e);
      setErr(e?.message || "Failed to build route.");
    } finally {
      if (estimateAbortRef.current.token === token) setEstimating(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-3">
        {/* Wider modal; form looks more “dashboard professional” */}
        <div className="w-full max-w-6xl max-h-[92vh] rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Route className="w-5 h-5 text-slate-700 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">Create a Route</div>
                <div className="text-[11px] text-slate-500 truncate">
                  Skips unrecognized addresses • Uses device location when Start = Current
                </div>
              </div>
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-50" title="Close">
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Body */}
          <div className="p-3 grid grid-cols-12 gap-3 overflow-hidden flex-1">
            {/* Left: Settings (two-column layout, less scrolling) */}
            <div className="col-span-12 md:col-span-7 overflow-y-auto pr-1 space-y-2">
              {err ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                  {err}
                </div>
              ) : null}

              {notice ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {notice}
                </div>
              ) : null}

              <Section title="Start & behavior">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <Field label="Start">
                    <select
                      value={startMode}
                      onChange={(e) => setStartMode(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
                    >
                      <option value="current">Current Location</option>
                      <option value="office">Office</option>
                      <option value="warehouse">Warehouse</option>
                      <option value="custom">Custom…</option>
                    </select>
                  </Field>

                  <Toggle
                    label="Return to start"
                    checked={returnToStart}
                    onChange={(e) => setReturnToStart(e.target.checked)}
                    hint="Round trip back to start."
                  />
                </div>

                {startMode === "office" ? (
                  <div className="mt-3">
                    <Field label="Office address">
                      <input
                        value={officeAddress}
                        onChange={(e) => setOfficeAddress(e.target.value)}
                        placeholder="Office address"
                        className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
                      />
                    </Field>
                  </div>
                ) : null}

                {startMode === "warehouse" ? (
                  <div className="mt-3">
                    <Field label="Warehouse address">
                      <input
                        value={warehouseAddress}
                        onChange={(e) => setWarehouseAddress(e.target.value)}
                        placeholder="Warehouse address"
                        className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
                      />
                    </Field>
                  </div>
                ) : null}

                {startMode === "custom" ? (
                  <div className="mt-3">
                    <Field label="Custom start address">
                      <input
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        placeholder="Start address"
                        className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
                      />
                    </Field>
                  </div>
                ) : null}

                <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <Toggle
                    label="Open in same tab"
                    checked={openSameTab}
                    onChange={(e) => setOpenSameTab(e.target.checked)}
                    hint="Best for mobile (launches Maps app)."
                  />
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[11px] text-slate-600">Start summary</div>
                    <div className="text-sm font-medium text-slate-900">{getOriginLabel()}</div>
                  </div>
                </div>
              </Section>

              <Section title="Filters">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <Field label="Territory">
                    <select
                      value={territoryId}
                      onChange={(e) => setTerritoryId(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
                    >
                      <option value="">All / manual pick</option>
                      {territories.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name || t.id}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Toggle
                    label="Auto-pick from territory"
                    checked={autoPickFromTerritory}
                    onChange={(e) => setAutoPickFromTerritory(e.target.checked)}
                    hint="Selects all matching accounts."
                  />

                  <Field
                    label="Samples"
                    hint={loadingSamples ? "Checking sampleOrders…" : "Based on sampleOrders"}
                  >
                    <select
                      value={sampleFilter}
                      onChange={(e) => setSampleFilter(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
                    >
                      <option value="any">Any</option>
                      <option value="with">Only with samples</option>
                      <option value="without">Only without samples</option>
                    </select>
                  </Field>

                  <Toggle
                    label="Follow up only"
                    checked={followUpOnly}
                    onChange={(e) => setFollowUpOnly(e.target.checked)}
                  />
                </div>
              </Section>

              <Section title="Limits & optimization">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <Field label="Max stops" hint="Hard cap for the route builder.">
                    <select
                      value={maxStops}
                      onChange={(e) => setMaxStops(Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
                    >
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                    </select>
                  </Field>

                  <Toggle
                    label="Let Google optimize order"
                    checked={optimizeStops}
                    onChange={(e) => setOptimizeStops(e.target.checked)}
                    hint="Reorders stops for best route."
                  />

                  <Field
                    label="Max travel time (minutes)"
                    hint="If set, we estimate time and trim stops to fit. (Needs location if Start=Current.)"
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <input
                        type="number"
                        min={0}
                        step={5}
                        value={maxTravelMinutes}
                        onChange={(e) => setMaxTravelMinutes(Number(e.target.value) || 0)}
                        placeholder="0"
                        className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
                      />
                    </div>
                  </Field>

                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[11px] text-slate-600">Stops selected</div>
                    <div className="text-sm font-semibold text-slate-900">
                      {stops.length} / {effectiveMaxStops}
                    </div>
                    {lastEstimate ? (
                      <div className="mt-1 text-[11px] text-slate-600">
                        Estimate:{" "}
                        <b className="text-slate-900">
                          {lastEstimate.minutes ? `${lastEstimate.minutes} min` : "—"}
                        </b>{" "}
                        • Used:{" "}
                        <b className="text-slate-900">{lastEstimate.usedStops}</b>{" "}
                        {lastEstimate.capped ? <span className="text-amber-700">• trimmed</span> : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleBuildRoute}
                  disabled={estimating}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-sm font-semibold disabled:opacity-70"
                >
                  {estimating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Route className="w-4 h-4" />}
                  {estimating ? "Building…" : "Open Google Maps route"}
                </button>
              </Section>
            </div>

            {/* Right: Accounts */}
            <div className="col-span-12 md:col-span-5 overflow-hidden">
              <div className="rounded-xl border border-slate-200 overflow-hidden h-full flex flex-col">
                <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-slate-700">Accounts</div>
                    <div className="text-[11px] text-slate-500">Showing {filteredAccounts.length}</div>
                  </div>

                  <div className="mt-2 relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search name, contact, address…"
                      className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
                    />
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto">
                  {loading ? (
                    <div className="p-4 flex items-center gap-2 text-slate-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Loading…</span>
                    </div>
                  ) : filteredAccounts.length === 0 ? (
                    <div className="p-4 text-sm text-slate-600">No accounts match these filters.</div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {filteredAccounts.map((a) => {
                        const checked = selectedAccountIds.includes(a.id);
                        const label = getAccountLabel(a);
                        const addr = accountToAddress(a);
                        const contact = accountContactLine(a);
                        const sampleCount = sampleCountsByAccount[a.id] || 0;
                        const follow = isFollowUpAccount(a);

                        const line2 = [contact, addr].map(norm).filter(Boolean).join(" • ");

                        return (
                          <div key={a.id} className="flex items-start gap-2 px-3 py-2 hover:bg-slate-50">
                            <label className="pt-[3px] flex-shrink-0">
                              <input type="checkbox" checked={checked} onChange={() => toggleSelected(a.id)} />
                            </label>

                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="text-sm font-medium text-slate-900 whitespace-normal break-words leading-snug">
                                  {label}
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {sampleCount > 0 ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-700">
                                      <Package className="w-3.5 h-3.5 text-slate-400" />
                                      <span className="tabular-nums">{sampleCount}</span>
                                    </span>
                                  ) : null}

                                  {follow ? (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800">
                                      Follow Up
                                    </span>
                                  ) : null}

                                  {checked ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : null}
                                </div>
                              </div>

                              {line2 ? (
                                <div className="mt-0.5 text-xs text-slate-500 whitespace-normal break-words leading-snug">
                                  {line2}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="px-3 py-2 border-t border-slate-200 bg-white flex items-center justify-between">
                  <div className="text-[11px] text-slate-500 truncate">
                    Logged in as: <span className="font-mono">{me?.email || me?.uid || "unknown"}</span>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <div className="mt-2 text-[11px] text-slate-500">
                Tip: If stops get skipped, fix those addresses in the account record and rebuild.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom confirm modal for same-tab navigation when stops are skipped */}
      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onCancel={() => setConfirmState({ open: false, title: "", message: "", onConfirm: null })}
        onConfirm={() => {
          if (typeof confirmState.onConfirm === "function") confirmState.onConfirm();
        }}
      />
    </>
  );
}
